// =============================================================================
// features/assistant/history.ts — Monta o `history` enviado ao copiloto
// interno a partir dos turnos do chat (F6-S19, memória de sessão).
//
// Regras (doc 22 + apps/api/src/modules/internal-assistant/schemas.ts):
//   - Só turnos com status 'success' entram (erro/loading são excluídos).
//   - O turno atual (excludeTurnId) nunca entra — ele ainda não tem resposta
//     no momento em que o history é montado para a request em andamento.
//   - Ordem cronológica, alternando 'user' (pergunta) e 'assistant' (resposta)
//     por turno.
//   - Cap rígido de ASSISTANT_HISTORY_MAX_TURNS itens (backend rejeita com 400
//     acima disso) — sempre os últimos, nunca os primeiros.
//   - Cap de ASSISTANT_HISTORY_MAX_CONTENT_CHARS caracteres POR turno. O
//     `answer` do copiloto não tem limite de tamanho na geração, e o contrato do
//     grafo Python corta em 4000: sem este clamp, uma única resposta longa fazia
//     todo envio seguinte da conversa voltar 400 — a conversa travava até o
//     usuário fechar o modal (incidente de 2026-08-22).
//
// Função pura, sem I/O — nada aqui persiste em localStorage/sessionStorage
// (LGPD doc 17): o histórico vive só no useState do AssistantWorkspaceModal e
// desaparece ao fechar/desmontar o modal.
// =============================================================================

import {
  ASSISTANT_HISTORY_MAX_CONTENT_CHARS,
  ASSISTANT_HISTORY_MAX_TURNS,
  type AssistantHistoryTurn,
} from '../../hooks/assistant/useAssistantQuery';

import type { AssistantTurn } from './types';

/**
 * Constrói o `history` a enviar ao backend a partir dos turnos anteriores do
 * chat, excluindo o turno atual (`excludeTurnId`) e qualquer turno que não
 * tenha terminado com sucesso.
 */
/** Marcador de corte — sinaliza ao modelo que o turno veio incompleto. */
const TRUNCATION_MARKER = '… [trecho anterior truncado]';

/**
 * Corta um turno no teto de caracteres, preservando o INÍCIO.
 *
 * O começo da resposta do copiloto traz o resumo e a conclusão; a cauda é
 * detalhamento. Cortar o fim perde menos contexto do que cortar o começo.
 */
export function clampHistoryContent(content: string): string {
  if (content.length <= ASSISTANT_HISTORY_MAX_CONTENT_CHARS) return content;
  return (
    content.slice(0, ASSISTANT_HISTORY_MAX_CONTENT_CHARS - TRUNCATION_MARKER.length) +
    TRUNCATION_MARKER
  );
}

export function buildAssistantHistory(
  turns: AssistantTurn[],
  excludeTurnId: string,
): AssistantHistoryTurn[] {
  const items: AssistantHistoryTurn[] = [];

  for (const turn of turns) {
    if (turn.id === excludeTurnId) continue;
    if (turn.status !== 'success' || !turn.answer) continue;

    items.push({ role: 'user', content: clampHistoryContent(turn.question) });
    items.push({ role: 'assistant', content: clampHistoryContent(turn.answer) });
  }

  return items.slice(-ASSISTANT_HISTORY_MAX_TURNS);
}
